import { useMutation } from '@apollo/client';
import { useStripe } from '@stripe/react-stripe-js';
import { useTranslations } from '@vocab/react';
import { useAtomValue } from 'jotai';

import { useAnalytics } from '../../context/AnalyticsProvider';
import type { Country } from '../../entities/Country';
import type { Currency } from '../../entities/Currency';
import { PaymentIntentErrorCode, type UseCase } from '../../entities/enums';
import {
  CREATE_STRIPE_PAYMENT_INTENT,
  CREATE_STRIPE_INVOICE_PAYMENT_INTENT,
} from '../../graphql';
import { useGenerateIdempotencyKey } from '../../hook/useGenerateIdempotencyKey';
import translations from '../../locales.vocab';
import type { HirerPaymentResult, Invoice, PaymentStatus } from '../../types';
import {
  PaymentDefaultError,
  PaymentIntentExpire,
} from '../Errors/customErrors';
import { hidePayNowQrAtom } from '../Maintenance/MaintenanceStore';

interface InvoiceInputs {
  customerId: string;
  accountId: string;
  prn: string;
  invoice: Invoice[];
  email: string;
  totalForPaymentIntent: number;
}

interface CajaInputs {
  orderId: string;
}

export interface UsePayNowQrInput<T> {
  country: Country;
  currency: Currency;
  invoiceInputs: T extends 'INVOICE' ? InvoiceInputs : undefined;
  cajaInputs: T extends 'CAJA' ? CajaInputs : undefined;
}

const usePayNowQr = ({
  invoiceInputs = {
    customerId: '',
    accountId: '',
    prn: '',
    invoice: [],
    email: '',
    totalForPaymentIntent: 0,
  },
  cajaInputs = {
    orderId: '',
  },
}: UsePayNowQrInput<UseCase>) => {
  const { prn, customerId, accountId, invoice, email, totalForPaymentIntent } =
    invoiceInputs;

  const { orderId } = cajaInputs;

  const stripe = useStripe();

  const { t } = useTranslations(translations);
  const { sendLinkPaymentProcessingEvent } = useAnalytics();
  const [createStripePaymentIntent] = useMutation(CREATE_STRIPE_PAYMENT_INTENT);
  const [createStripePaymentIntentForInvoice] = useMutation(
    CREATE_STRIPE_INVOICE_PAYMENT_INTENT,
  );
  const isMaintenance = useAtomValue(hidePayNowQrAtom);
  const { idempotencyKey, generateNewIdempotencyKey } =
    useGenerateIdempotencyKey();

  const submitPayNowQrPayment = async (): Promise<HirerPaymentResult> => {
    if (!stripe) {
      throw new PaymentDefaultError('Stripe is not loaded');
    }
    if (isMaintenance) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }
    const intentResponse = await createStripePaymentIntent({
      variables: {
        input: {
          orderId,
          paymentMethod: 'paynow',
          idempotencyKey,
        },
      },
    });

    const stripePaymentIntentPayload =
      intentResponse.data?.createStripePaymentIntent;

    if (
      stripePaymentIntentPayload.__typename !== 'StripePaymentIntentPayload'
    ) {
      throw new PaymentDefaultError(stripePaymentIntentPayload.message);
    }

    // Scalability: skip payment gateway call
    if (
      stripePaymentIntentPayload.clientSecret === 'test-record-clientsecret'
    ) {
      return {
        status: 'succeeded' as PaymentStatus,
      };
    }

    const startTime = performance.now();
    const stripeResponse = await stripe.confirmPayNowPayment(
      stripePaymentIntentPayload.clientSecret,
    );

    const endTime = performance.now();
    sendLinkPaymentProcessingEvent('PayNowQR', endTime - startTime);

    if (stripeResponse.error) {
      throw new PaymentDefaultError(stripeResponse.error.message as string);
    } else if (
      stripeResponse.paymentIntent &&
      stripeResponse.paymentIntent.last_payment_error &&
      stripeResponse.paymentIntent.last_payment_error.code ===
        PaymentIntentErrorCode.paymentIntentExpired
    ) {
      generateNewIdempotencyKey();
      const expireMessage = t('qr.expire.error');
      throw new PaymentIntentExpire(expireMessage as string);
    }

    return {
      paymentIntentId: stripeResponse.paymentIntent.id,
      status: stripeResponse.paymentIntent.status,
    };
  };

  const submitPayNowQrPaymentForInvoices =
    async (): Promise<HirerPaymentResult> => {
      if (!stripe) {
        throw new PaymentDefaultError('Stripe is not loaded');
      }
      if (isMaintenance) {
        throw new PaymentDefaultError('Payment method is in maintenance');
      }

      // temporarily update id to reference, will change it after BE update
      const invoices = invoice!.map(({ id, ...rest }) => ({
        reference: id,
        ...rest,
      }));

      const intentResponse = await createStripePaymentIntentForInvoice({
        variables: {
          seekBillingAccountId: customerId, // TODO: need to update graphql to take in customerId instead of seekBillingAccountId
          seekAdvertiserId: accountId, // TODO: need to update graphql to take in accountId instead of seekAdvertiserId
          paymentMethod: 'paynow',
          totalForPaymentIntent,
          invoices,
          email,
          prn,
          idempotencyKey,
        },
      });

      const stripePaymentIntentPayload =
        intentResponse.data?.createStripePaymentIntentForInvoice;

      if (
        stripePaymentIntentPayload.__typename !== 'StripePaymentIntentPayload'
      ) {
        throw new PaymentDefaultError(stripePaymentIntentPayload.message);
      }

      // Scalability: skip payment gateway call
      if (
        stripePaymentIntentPayload.clientSecret === 'test-record-clientsecret'
      ) {
        return {
          status: 'succeeded' as PaymentStatus,
        };
      }

      const startTime = performance.now();
      const stripeResponse = await stripe.confirmPayNowPayment(
        stripePaymentIntentPayload.clientSecret,
      );

      const endTime = performance.now();
      sendLinkPaymentProcessingEvent('PayNowQR', endTime - startTime);

      if (stripeResponse.error) {
        throw new PaymentDefaultError(stripeResponse.error.message as string);
      } else if (
        stripeResponse.paymentIntent &&
        stripeResponse.paymentIntent.last_payment_error &&
        stripeResponse.paymentIntent.last_payment_error.code ===
          PaymentIntentErrorCode.paymentIntentExpired
      ) {
        generateNewIdempotencyKey();
        const expireMessage = t('qr.expire.error');
        throw new PaymentIntentExpire(expireMessage as string);
      }

      return {
        paymentIntentId: stripeResponse.paymentIntent.id,
        status: stripeResponse.paymentIntent.status,
      };
    };

  return {
    submitPayNowQrPayment,
    submitPayNowQrPaymentForInvoices,
  };
};

export default usePayNowQr;
